import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import mapboxgl from "mapbox-gl";
import "./MapStyle.css";
import {
  Airport,
  Banques,
  Cart,
  Hospital,
  Restaurants,
  School,
} from "./MapSVGS";

const MarkerSVG = () => (
  <svg
    width="32"
    height="40"
    viewBox="0 0 88 106"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g filter="url(#filter0_d_2001_2)">
      <path
        d="M84.5254 40.7407C84.5254 63.2412 54.0169 100 43.8475 100C32.7535 100 3.16949 63.2412 3.16949 40.7407C3.16949 18.2403 21.3816 0 43.8475 0C66.3133 0 84.5254 18.2403 84.5254 40.7407Z"
        fill="black"
      />
    </g>
    <circle cx="43.8983" cy="40.8983" r="33.8983" fill="white" />
    <defs>
      <filter
        id="filter0_d_2001_2"
        x="0.169495"
        y="0"
        width="87.3559"
        height="106"
        filterUnits="userSpaceOnUse"
        colorInterpolationFilters="sRGB"
      >
        <feFlood floodOpacity="0" result="BackgroundImageFix" />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset dy="3" />
        <feGaussianBlur stdDeviation="1.5" />
        <feComposite in2="hardAlpha" operator="out" />
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0"
        />
        <feBlend
          mode="normal"
          in2="BackgroundImageFix"
          result="effect1_dropShadow_2001_2"
        />
        <feBlend
          mode="normal"
          in="SourceGraphic"
          in2="effect1_dropShadow_2001_2"
          result="shape"
        />
      </filter>
    </defs>
  </svg>
);

// marker component receives a Map() instance, a GeoJSON point feature representing a Point of Interest, and a category string to determine which icon to display in the marker
const POIMarker = ({ map, feature, category, locations, onItineraryClick }) => {
  const { geometry, properties } = feature;

  // ref for the Marker() instance, used to manage the marker lifecycle and remove it from the map
  const markerRef = useRef(null);
  // store the category in a state variable to use only its initial value
  const [emojiCategory] = useState(category);
  // State for the program selection modal
  const [showModal, setShowModal] = useState(false);

  // refs for the DOM nodes to be used for the marker and popup content
  const markerContentRef = useRef(document.createElement("div"));
  const popupContentRef = useRef(document.createElement("div"));

  // Create a modal container if it doesn't exist
  useEffect(() => {
    // Check if modal container exists
    let modalContainer = document.getElementById("poi-modal-container");
    if (!modalContainer) {
      modalContainer = document.createElement("div");
      modalContainer.id = "poi-modal-container";
      document.body.appendChild(modalContainer);
    }

    return () => {
      // Don't remove the container on unmount as other markers might use it
    };
  }, []);

  // Function to handle itinerary button click
  const handleItineraryClick = () => {
    if (locations && locations.length === 1) {
      onItineraryClick(locations[0], geometry.coordinates);
    } else {
      onItineraryClick(null, geometry.coordinates);
    }
  };

  // when the component mounts, create a new marker and popup and add them to the map
  useEffect(() => {
    // instantiate a new Mapbox Popup()
    const popup = new mapboxgl.Popup({
      closeButton: false,
      closeOnMove: true,
      offset: 40,
    }).setDOMContent(popupContentRef.current); // use the popupContentRef as the content of the popup

    // instantiate a new Mapbox Marker()
    markerRef.current = new mapboxgl.Marker(markerContentRef.current, {
      // use the markerContentRef as the content of the marker
      anchor: "bottom",
    })
      .setLngLat(geometry.coordinates) // set the marker's position using the coordinates from the feature
      .setPopup(popup) // set the popup to be displayed when the marker is clicked
      .addTo(map); // add the marker to the map

    // cleanup function: remove marker when component unmounts
    return () => {
      markerRef.current.remove();
    };
  }, []);

  // assign an emoji based on the category
  let emoji;
  switch (emojiCategory) {
    case "high_school,elementary_school,college,kindergarten":
      emoji = <School size={18} />;
      break;
    case "hospital":
      emoji = <Hospital size={16} />;
      break;
    case "airport_terminal":
      emoji = <Airport size={20} />;
      break;
    case "supermarket":
      emoji = <Cart size={16} />;
      break;
    case "restaurant":
      emoji = <Restaurants size={16} />;
      break;
    case "bank":
      emoji = <Banques size={20} />;
      break;
    default:
      emoji = "📍";
  }

  // render nothing directly into the component tree,
  // instead, use React portals to inject content into the DOM nodes displayed in the marker and popup.
  return (
    <>
      {/* Portal 1: Popup content, rendered into the DOM node for the popup */}
      {createPortal(
        <div>
          <div className="popup-title">{properties.name}</div>
          <div className="popup-address">
            {properties.full_address || properties.address}
          </div>
          <button className="itinerary-button" onClick={handleItineraryClick}>
            Itinéraire
          </button>
        </div>,
        popupContentRef.current
      )}

      {/* Portal 2: Marker content, rendered into the DOM node for the marker */}
      {createPortal(
        <>
          <MarkerSVG />
          <div className="marker-emoji">{emoji}</div>
        </>,
        markerContentRef.current
      )}

      {/* Portal 3: Modal for selecting a property - rendered directly to body */}
      {showModal &&
        createPortal(
          <div
            className="program-selection-modal"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
            }}
          >
            <div className="program-selection-content">
              <div className="modal-header">Choisir une propriété</div>
              <div className="programme-modal-body">
                {locations &&
                  locations.map((location) => (
                    <div
                      key={location.documentId}
                      className="property-item"
                      onClick={() => {
                        setShowModal(false);
                        onItineraryClick(location, geometry.coordinates);
                      }}
                    >
                      {location.name || "Propriété"}
                    </div>
                  ))}
              </div>
              <button
                className="modal-close-button"
                onClick={() => setShowModal(false)}
              >
                Fermer
              </button>
            </div>
          </div>,
          document.getElementById("poi-modal-container")
        )}
    </>
  );
};

export default POIMarker;
